import { useTheme } from "@mui/material";
import { Alert, DataGrid } from "@platform/design-system";
import { AllEnterpriseModule } from "ag-grid-enterprise";
import { useCallback, useMemo } from "react";

import gridIcons from "../../gridIcons";

import type {
  CellValueChangedEvent,
  ColDef,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GetRowIdParams,
  GridState,
  IFiltersToolPanel,
  MenuItemDef,
  SideBarDef,
  StateUpdatedEvent,
  ToolPanelVisibleChangedEvent,
} from "ag-grid-community";
import type { ResponsesGridRow } from "./types";

/**
 * Grid persistence wiring sourced from `useMpxAgGridState` (the
 * mpx-backed `gridProps` it returns). Optional so Storybook and other
 * non-persistent contexts can still render the grid.
 */
export interface CarrierResponsesGridStateProps {
  key?: number | string;
  initialState?: GridState;
  onStateUpdated?: (event: StateUpdatedEvent) => void;
}

export interface CarrierResponsesGridProps {
  rowData: ResponsesGridRow[];
  colDefs: ColDef<ResponsesGridRow>[];
  /**
   * Wires server-side persistence for the user's view of this grid.
   * When omitted, the grid renders without persistence.
   */
  gridStateProps?: CarrierResponsesGridStateProps;
  /** Invoked when an analyst selects a value from a coverage cell dropdown. */
  onCellValueChanged?: (event: CellValueChangedEvent<ResponsesGridRow>) => void;
  /** Passed through to AG Grid; consumed by cell renderers (e.g. cell reviews). */
  context?: unknown;
  getContextMenuItems?: (
    params: GetContextMenuItemsParams,
  ) => (DefaultMenuItem | MenuItemDef)[];
}

/**
 * Presentational AG Grid component for the carrier-by-coverage responses matrix.
 *
 * Receives pre-built `rowData` and `colDefs` from `useResponsesTable` or from
 * Storybook fixture injection. Does not fetch data itself.
 */
export const CarrierResponsesGrid = ({
  rowData,
  colDefs,
  gridStateProps,
  onCellValueChanged,
  context,
  getContextMenuItems,
}: CarrierResponsesGridProps): React.JSX.Element => {
  const muiTheme = useTheme();

  const defaultColDef = useMemo<ColDef<ResponsesGridRow>>(
    () => ({
      resizable: true,
      sortable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        display: "flex",
        alignItems: "center",
      },
    }),
    [],
  );

  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "coverages",
          labelDefault: "Coverages",
          labelKey: "coverages",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressColumnExpandAll: true,
            suppressPivotMode: true,
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
          },
        },
        {
          id: "carriers",
          labelDefault: "Carriers",
          labelKey: "carriers",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          toolPanelParams: {
            suppressExpandAll: true,
            suppressFilterSearch: true,
          },
        },
      ],
    }),
    [],
  );

  const handleToolPanelVisibleChanged = useCallback(
    (event: ToolPanelVisibleChangedEvent) => {
      if (event.key === "carriers" && event.visible) {
        event.api
          .getToolPanelInstance<IFiltersToolPanel>("carriers")
          ?.expandFilters();
      }
    },
    [],
  );

  // Stable row IDs are required for the persisted row-order list to
  // line up across renders. `carrierId` is the natural key.
  const getRowId = useCallback(
    (params: GetRowIdParams<ResponsesGridRow>) => params.data.carrierId,
    [],
  );

  if (rowData.length === 0) {
    return (
      <Alert data-testid="carrier-responses-grid" severity="info">
        {colDefs.length > 1
          ? "No carriers found for this RFP."
          : "No carriers or coverages found for this RFP."}
      </Alert>
    );
  }

  return (
    <DataGrid
      key={gridStateProps?.key}
      containerProps={{ "data-testid": "carrier-responses-grid" }}
      modules={[AllEnterpriseModule]}
      context={context}
      getContextMenuItems={getContextMenuItems}
      sx={{
        width: "100%",
        overflow: "hidden",
        "& .ag-header-container .ag-cell-label-container .ag-header-cell-label":
          {
            justifyContent: "flex-start",
          },
        "& .ag-header-cell-text": {
          overflow: "visible",
          whiteSpace: "normal",
          textOverflow: "unset",
          textAlign: "left",
        },
        "& .ag-header-cell[col-id='carrierName'].ag-header-cell-filtered .ag-filter-icon":
          {
            display: "none",
          },
      }}
      rowData={rowData}
      columnDefs={colDefs}
      defaultColDef={defaultColDef}
      domLayout="autoHeight"
      headerHeight={60}
      suppressMovableColumns
      suppressHorizontalScroll={false}
      icons={gridIcons}
      sideBar={sideBar}
      themeParams={{
        textColor: muiTheme.palette.text.primary,
        headerTextColor: muiTheme.palette.text.primary,
      }}
      getRowId={getRowId}
      initialState={gridStateProps?.initialState}
      onStateUpdated={gridStateProps?.onStateUpdated}
      onToolPanelVisibleChanged={handleToolPanelVisibleChanged}
      onCellValueChanged={onCellValueChanged}
    />
  );
};
